/* eslint-disable react-hooks/exhaustive-deps */
"use client";
import { useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import useFacebookPixel from "./hooks/useFacebookPixel";
import useGoogleAnalytics from "./hooks/useGoogleAnalytics";
import useGoogleTagManager from "./hooks/useGoogleTagManager";
import useSmartlook from "./hooks/useSmartlook";
import useSnapchatPixel from "./hooks/useSnapchatPixel";
import useXPixel from "./hooks/useXPixel";
import { useGetCompanyQuery } from "@/api/AppApi/services/company";
import { useAtom } from "jotai";
import { analyticsHelpersIds } from "@/hooks/analytics-helpers-ids";
import useTiktokPixel from "./hooks/useTiktokPixel";
import useShouldFire from "./hooks/useShouldFire";
import { isSignedInAtom } from "@/hooks/authAtoms";
import { useGetProfileQuery } from "@/api/AppApi/services/auth";
import { toSHA256Hex } from "@/utils/functions/toSHA256Hex";
import { getAgentIdCookie } from "@/utils/functions/cookieOperations";
import {
  checkoutResultAtom,
  checkoutResultDataLayerAtom,
} from "@/hooks/checkoutResultAtom";
import { getFirstAndLastName } from "./nameParts";
import { getEventNameForTracker } from "./hooks/useEventTrackers/helpers/functions";
import {
  EventType,
  Tracker,
} from "./hooks/useEventTrackers/helpers/interfaces";
import { usePathname } from "next/navigation";

const PageViewPixel = () => {
  const getCompany = useGetCompanyQuery();

  const settings = getCompany?.data?.result?.theme?.settings;
  const xPixelEventIds = settings
    ? {
        x_page_view_event_id: settings.x_page_view_event_id,
        x_content_view_event_id: settings.x_content_view_event_id,
        x_add_to_cart_event_id: settings.x_add_to_cart_event_id,
        x_lead_event_id: settings.x_lead_event_id,
        x_purchase_event_id: settings.x_purchase_event_id,
        x_add_payment_info_event_id: settings.x_add_payment_info_event_id,
      }
    : undefined;
  const shouldFire = useShouldFire();
  const pathname = usePathname();
  const [refresh, setRefresh] = useState(false);
  const [, setaAnalyticsHelpersIds] = useAtom(analyticsHelpersIds);
  const { theme } = getCompany?.data?.result! ?? {};
  const {
    smartlook_code,
    snapchat_code,
    facebook_code,
    google_analytics_id,
    google_tag,
    tiktok_code,
    x_pixel_id,
    x_page_view_event_id,
  } = theme?.settings! ?? {};
  const [checkoutResultDataLayer] = useAtom(checkoutResultDataLayerAtom);
  const [checkoutResult] = useAtom(checkoutResultAtom);
  const userDataLayer = !checkoutResultDataLayer?.isPurchaseEventTracked
    ? checkoutResultDataLayer?.userdata
    : undefined;
  const [isSignedIn] = useAtom(isSignedInAtom);
  const { data: userProfile } = useGetProfileQuery();

  const prepareCustomerDataForInit = () => {
    const customerData: any = {};

    const profile = userProfile?.result! ?? {};
    if (isSignedIn && userProfile?.result) {
      if (profile.name) {
        const { firstName, lastName } = getFirstAndLastName(profile.name);
        customerData.first_name = firstName;
        customerData.last_name = lastName;
      }
      if (profile.email) {
        customerData.email = profile.email;
        customerData.email_hash = toSHA256Hex(profile.email);
      }
      if (profile.mobile) {
        customerData.phone = profile.mobile;
        customerData.phone_hash = toSHA256Hex(profile.mobile);
      }
    } else if (userDataLayer && shouldFire) {
      if (userDataLayer.email) {
        customerData.email = userDataLayer.email;
        customerData.email_hash = toSHA256Hex(userDataLayer.email);
      }
      if (userDataLayer.phone) {
        customerData.phone = userDataLayer.phone;
        customerData.phone_hash = toSHA256Hex(userDataLayer.phone);
      }
      if (userDataLayer.name) {
        const { firstName, lastName } = getFirstAndLastName(userDataLayer.name);
        customerData.first_name = firstName;
        customerData.last_name = lastName;
      }
    }
    if (checkoutResult?.cities_names) {
      customerData.city_name = checkoutResult?.cities_names.join(",");
    }
    if (checkoutResult?.areas_names) {
      customerData.area_name = checkoutResult?.areas_names.join(",");
    }
    if (checkoutResult?.countries_names) {
      customerData.country_name = checkoutResult?.countries_names.join(",");
    }

    if (typeof window !== "undefined") {
      const agentId = getAgentIdCookie();
      if (agentId) {
        customerData.external_id = agentId;
      }
    }

    return customerData;
  };

  const advancedMatchingData = prepareCustomerDataForInit();

  // One event_id per page view (regenerated on each navigation). Shared between
  // the browser PageView pixel and the server-side CAPI call for dedup.
  const pageViewEventId = useMemo(() => uuidv4(), [pathname, shouldFire]);

  // Raw (unhashed) user data matching the pixel's advanced-matching source:
  // the signed-in profile, else guest checkout data. Sent to the backend
  // page-view endpoint "if we have it".
  const pageViewUserData = useMemo(() => {
    const profile = userProfile?.result;
    if (isSignedIn && profile) {
      return {
        name: profile.name,
        email: profile.email,
        mobile: profile.mobile,
      };
    }
    if (userDataLayer) {
      return {
        name: userDataLayer.name,
        email: userDataLayer.email,
        mobile: userDataLayer.phone,
      };
    }
    return {} as { name?: string; email?: string; mobile?: string };
  }, [isSignedIn, userProfile, userDataLayer]);

  useFacebookPixel(
    facebook_code,
    refresh,
    advancedMatchingData,
    pageViewEventId,
  );
  useSnapchatPixel(snapchat_code, refresh);
  useTiktokPixel(tiktok_code, refresh, advancedMatchingData);
  useSmartlook(smartlook_code, refresh);
  useGoogleAnalytics(google_analytics_id, refresh);
  useGoogleTagManager(google_tag, refresh);
  const xEvent = useXPixel({ pageViewEventId: x_page_view_event_id });

  useEffect(() => {
    if (shouldFire) {
      setaAnalyticsHelpersIds({
        smartlook_code,
        snapchat_code,
        facebook_code,
        google_analytics_id,
        google_tag,
        x_pixel_id: x_pixel_id || "",
      });
    }
  }, [theme, shouldFire]);
  useEffect(() => {
    if (shouldFire && !refresh) {
      setRefresh(true);
    }
  }, [shouldFire]);
  useEffect(() => {
    if (shouldFire) {
      xEvent.trackEvent(
        getEventNameForTracker(EventType.PAGE_VIEW, Tracker.X, xPixelEventIds),
        {},
      );
    }
  }, [shouldFire, pathname]);

  // Tell the backend to fire the server-side (Conversions API) PageView on
  // every page view, reusing pageViewEventId (the same id the browser pixel
  // uses) for deduplication. Fire-and-forget; the route handler reads fbc/fbp
  // from cookies and calls the backend server-side.
  useEffect(() => {
    if (!shouldFire) return;
    // fbc/fbp are NOT sent from here: the _fbc/_fbp cookies are HttpOnly
    // (invisible to client JS) but are sent automatically with this same-origin
    // request, so the route handler reads them server-side.
    fetch("/api/page-view", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        event_id: pageViewEventId,
        name: pageViewUserData.name,
        email: pageViewUserData.email,
        mobile: pageViewUserData.mobile,
      }),
      keepalive: true,
    }).catch(() => {});
  }, [shouldFire, pathname, pageViewEventId]);

  if (!shouldFire) return;
  return <></>;
};

export default PageViewPixel;
